import React from "react";
import { TextField } from "Components/Base/Inputs/TextField";
import { InputAdornment } from "Components/Base/Inputs/InputAdornment";
import { IconButton } from "Components/Base/Inputs/IconButton";
import { Clear as ClearIcon, Search as SearchIcon } from "Components/Icons";
import styled from "styled-components";

/**
 * SearchField Component
 *
 * A custom search field component with an integrated clear button and search icon.
 * The component supports two search modes:
 * - onType: Search is triggered while typing (default behavior)
 * - onSubmit: Search is triggered only when explicitly submitted via search button, Enter key, or blur event
 *
 * @component
 * @example
 * import SearchField from "./SearchField";
 *
 * function MyComponent() {
 *   const [searchValue, setSearchValue] = useState("");
 *
 *   const handleSearch = () => {
 *     // Search logic
 *   };
 *
 *   return (
 *     <SearchField
 *       placeholder="Search..."
 *       value={searchValue}
 *       onChange={(e) => setSearchValue(e.target.value)}
 *       onSearch={handleSearch}
 *       clearLabel="Clear search"
 *       searchLabel="Perform search"
 *       searchMode="onSubmit"
 *     />
 *   );
 * }
 *
 * @param {string} props.placeholder - Placeholder text for the search input
 * @param {string} [props.ariaLabel] - Accessibility label for the search input (defaults to placeholder text)
 * @param {string} props.clearLabel - Accessibility label for the clear button
 * @param {string} props.searchLabel - Accessibility label for the search button
 * @param {string} props.value - Current value of the search input
 * @param {function} props.onChange - Callback fired when the input value changes: (event: React.ChangeEvent<HTMLInputElement>) => void
 * @param {function} props.onClear - Callback fired when the clear button is clicked: () => void
 * @param {function} props.onSearch - Callback fired when search is triggered: () => void
 * @param {function} [props.onBlur] - Callback fired when input loses focus (only used in 'onSubmit' mode): (event: React.FocusEvent<HTMLInputElement>) => void
 * @param {string} [props.width] - Width of the search field in pixels. If not specified, component will use its natural width of 300px
 * @param {boolean} [props.fullWidth] - Whether the search field should take up the full width of its container
 * @param {'onType' | 'onSubmit'} [props.searchMode='onType'] - Search trigger mode
 * @param {Object} [props.sx] - MUI system props for additional styling
 *
 * @returns {JSX.Element} The SearchField component
 */

const StyledSearchIconTextField = styled(TextField)`
  .MuiInputBase-root {
    padding-right: 4px !important;
    ${({ width }) => width && `width: ${width};`}
  }
`;

const SearchField = ({
  placeholder,
  ariaLabel,
  clearLabel,
  searchLabel,
  value,
  onChange,
  onClear,
  onSearch,
  onBlur,
  width,
  searchMode = "onType",
  fullWidth,
  sx,
}) => {
  const displayClearButton = !!value;
  const displaySearchButton = searchMode === "onSubmit" || !value;

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && value && searchMode === "onSubmit") {
      onSearch();
    }
  };

  return (
    <StyledSearchIconTextField
      placeholder={placeholder}
      aria-label={ariaLabel ?? placeholder}
      value={value}
      onChange={onChange}
      onBlur={searchMode === "onSubmit" ? onBlur : undefined}
      onKeyPress={handleKeyPress}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {displayClearButton && (
              <IconButton
                title={value ? clearLabel : placeholder}
                onClick={onClear}
                icon={<ClearIcon />}
                disabled={!value}
              />
            )}
            {displaySearchButton && (
              <IconButton
                title={value ? searchLabel : placeholder}
                onClick={searchMode === "onType" ? onClear : onSearch}
                icon={<SearchIcon />}
                disabled={!value}
              />
            )}
          </InputAdornment>
        ),
      }}
      width={width}
      fullWidth={fullWidth}
      sx={sx}
    />
  );
};

export default SearchField;
